import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Alert,
  AlertIcon,
  Box,
  Button,
  Checkbox,
  Flex,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  SimpleGrid,
  Text,
  Tooltip,
} from '@chakra-ui/react';
import { useEffect, useMemo } from 'react';

import { TYPES_MAP } from '#constants';
import PermitModeSelector from '../Tracker/PermitModeSelector.jsx';
import { shallow } from 'zustand/shallow';
import { useUIStore } from '#store';

const NO_LOOP_KEY = '__no_loop__';

/** Group campsites by their `loop`, preserving order; loop-less sites bucket last. */
function groupByLoop(campsites) {
  const groups = new Map();
  for (const c of campsites || []) {
    const key = c.loop ? String(c.loop) : NO_LOOP_KEY;
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(c);
  }
  return Array.from(groups.entries()).map(([key, items]) => ({
    key,
    label: key === NO_LOOP_KEY ? 'Other campsites' : key,
    campsites: items,
  }));
}

function Campsite({ campsite, selected, onClick }) {
  return (
    <Flex
      onClick={() => onClick(campsite)}
      bgColor={selected ? 'brand.500' : 'white'}
      borderColor={selected ? 'brand.500' : 'brand.500'}
      borderWidth={2}
      minH={12}
      maxH={20}
      alignItems="center"
      justify={'center'}
      borderRadius={3}
      opacity={campsite.reservable ? 1 : 0.5}
      _hover={{
        cursor: 'pointer',
        opacity: campsite.reservable ? 0.9 : 0.5,
      }}
      boxShadow="md"
      fontSize={'0.85em'}
      p={4}
    >
      <Tooltip
        label="Can't be reserved"
        isDisabled={campsite.reservable}
        hasArrow
      >
        <Text
          textAlign="center"
          color={selected ? 'white' : 'brand.900'}
          noOfLines={3}
          overflow="hidden"
          fontWeight={500}
        >
          {campsite.name}
        </Text>
      </Tooltip>
    </Flex>
  );
}

function CampsiteGrid({ campsites, selectedCampsites, onClick }) {
  return (
    <SimpleGrid columns={[1, 3]} spacingX={2} spacingY={[1, 2]}>
      {campsites.map((c, idx) => (
        <Campsite
          campsite={c}
          key={c._id ?? idx}
          onClick={onClick}
          selected={selectedCampsites?.indexOf(c._id) > -1}
        />
      ))}
    </SimpleGrid>
  );
}

function CampsiteSelector() {
  const {
    campsites,
    addCampsitesToFilter,
    removeCampsitesFromFilter,
    trackerInput: { filters },
    setToast,
    outing,
  } = useUIStore(
    (state) => ({
      campsites: state.campsites,
      outing: state.outing,
      addCampsitesToFilter: state.addCampsitesToFilter,
      removeCampsitesFromFilter: state.removeCampsitesFromFilter,
      trackerInput: state.trackerInput,
      setToast: state.setToast,
    }),
    shallow
  );
  const selectedCampsites = filters.campsites;

  const groups = useMemo(() => groupByLoop(campsites), [campsites]);
  // Only nest into loop accordions when there's real loop structure.
  const hasLoops = groups.length > 1 || groups[0]?.key !== NO_LOOP_KEY;

  const onClick = (campsite) => {
    if (!campsite?.reservable) {
      setToast({
        title: `${TYPES_MAP[outing?.entity_type]} can't be reserved`,
        status: 'warning',
        duration: 2000,
        isClosable: true,
        position: 'top',
      });
      return;
    }
    const campIndex = selectedCampsites?.indexOf(campsite._id);
    if (campIndex > -1) {
      // Campsite was selected, de-select it
      removeCampsitesFromFilter(campsite._id);
    } else {
      addCampsitesToFilter(campsite._id);
    }
  };

  const toggleLoop = (group, allSelected) => {
    const reservableIds = group.campsites
      .filter((c) => c.reservable)
      .map((c) => c._id);
    if (allSelected) {
      reservableIds.forEach((id) => {
        if (selectedCampsites?.indexOf(id) > -1) removeCampsitesFromFilter(id);
      });
    } else {
      reservableIds.forEach((id) => {
        if (!(selectedCampsites?.indexOf(id) > -1)) addCampsitesToFilter(id);
      });
    }
  };

  if (!campsites?.length) {
    return (
      <Alert status="warning" variant="subtle" justifyContent={'center'}>
        <AlertIcon />
        There aren't any individual campsites available
      </Alert>
    );
  }

  if (!hasLoops) {
    return (
      <Box overflowY="auto" maxH="45vh" minH="150px">
        <CampsiteGrid
          campsites={campsites}
          selectedCampsites={selectedCampsites}
          onClick={onClick}
        />
      </Box>
    );
  }

  // Expand loops that already contain a selection (else the first loop).
  const defaultIndex = groups.reduce((acc, g, idx) => {
    if (g.campsites.some((c) => selectedCampsites?.indexOf(c._id) > -1)) {
      acc.push(idx);
    }
    return acc;
  }, []);
  if (defaultIndex.length === 0) defaultIndex.push(0);

  return (
    <Accordion allowMultiple defaultIndex={defaultIndex} overflowY="auto" maxH="45vh">
      {groups.map((group) => {
        const reservableIds = group.campsites
          .filter((c) => c.reservable)
          .map((c) => c._id);
        const selectedCount = reservableIds.filter(
          (id) => selectedCampsites?.indexOf(id) > -1
        ).length;
        const allSelected =
          reservableIds.length > 0 && selectedCount === reservableIds.length;
        const someSelected = selectedCount > 0 && !allSelected;

        return (
          <AccordionItem key={group.key}>
            <Flex align="center" pr={2}>
              <Checkbox
                colorScheme="brand"
                isChecked={allSelected}
                isIndeterminate={someSelected}
                isDisabled={reservableIds.length === 0}
                onChange={() => toggleLoop(group, allSelected)}
                ml={4}
                mr={1}
              />
              <AccordionButton _hover={{ bg: 'transparent' }} px={2}>
                <Box as="span" flex="1" textAlign="left" fontWeight={600}>
                  {group.label}
                </Box>
                <Text fontSize="sm" color="blackAlpha.600" mr={2}>
                  {selectedCount > 0
                    ? `${selectedCount}/${group.campsites.length}`
                    : group.campsites.length}
                </Text>
                <AccordionIcon />
              </AccordionButton>
            </Flex>
            <AccordionPanel pb={4}>
              <CampsiteGrid
                campsites={group.campsites}
                selectedCampsites={selectedCampsites}
                onClick={onClick}
              />
            </AccordionPanel>
          </AccordionItem>
        );
      })}
    </Accordion>
  );
}

export default function Campsites() {
  const {
    campsitesModal,
    toggleCampsitesModal,
    getCampsites,
    outing,
    resetCampsitesFilter,
    trackerInput,
  } = useUIStore(
    (state) => ({
      campsitesModal: state.campsitesModal,
      toggleCampsitesModal: state.toggleCampsitesModal,
      getCampsites: state.getCampsites,
      outing: state.outing,
      resetCampsitesFilter: state.resetCampsitesFilter,
      trackerInput: state.trackerInput,
    }),
    shallow
  );

  useEffect(() => {
    if (campsitesModal || trackerInput?.filters?.campsites) {
      // Only get the campsite when the modal opens
      // Or we already had campsites selected.
      getCampsites();
    }
  }, [outing?._id, campsitesModal]);

  return (
    <Modal
      isOpen={campsitesModal}
      onClose={() => toggleCampsitesModal(false)}
      size="3xl"
    >
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          Choose one or more {TYPES_MAP[outing?.entity_type]}
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody minH={200}>
          <PermitModeSelector />
          <CampsiteSelector />
        </ModalBody>
        <ModalFooter>
          <Button
            colorScheme="red"
            variant="ghost"
            mr={3}
            onClick={() => {
              resetCampsitesFilter();
              toggleCampsitesModal(false);
            }}
          >
            Remove filter
          </Button>
          <Button
            colorScheme="brand"
            mr={3}
            onClick={() => toggleCampsitesModal(false)}
          >
            Save Selection
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
